<template>
	<!-- currently only used for pinned questions -->
	<v-card
		style="background-color: rgb(var(--v-theme-contained))"
		:variant="isPinned() ? 'tonal' : 'elevated'"
		elevation="2"
	>
		<v-card-title style="max-width: 85%">
			<span class="multiline">{{ compProps.title }} </span>
			<div class="d-flex justify-end" style="position: absolute; top: 10px; right: 5px">
				<v-icon
					v-if="
						domainStore.hasAccessRights(UserRole.Editor) && (debugStore.altKeyHeld || debugStore.debugMode)
					"
					size="small"
					color="blue"
					class="ml-1"
					@click="openDesignerProcess"
				>
					mdi-link
				</v-icon>
			</div>
		</v-card-title>
		<v-card-subtitle v-if="compProps.subtitle">
			<div class="d-flex align-center">
				<AskQuestionButton
					v-if="displayPlayButton()"
					color=""
					:text="
						compProps.instanceTask?.context?.question_body_txt?.[domainStore.languagePreference] ||
						compProps.subtitle
					"
				/>
				<div class="markdown-content" v-html="marked.parse(compProps.subtitle)"></div>
			</div>
		</v-card-subtitle>
		<v-card-text>
			<question-input
				v-for="(attr, index) in outputAttributes"
				:key="attr.id"
				:instance-attribute-id="attr.id"
				:instance-task="compProps.instanceTask"
				:show-unknown-button="compProps.showUnknownButton"
				:style="{ 'margin-bottom': index < outputAttributes.length - 1 ? '10px' : '0px' }"
			/>
		</v-card-text>
	</v-card>
</template>

<script setup lang="ts">
import { InstanceAttribute, InstanceTask } from "@lr-notitia/notitia-operator/schemas";
import { PropType, computed, onMounted, onUnmounted } from "vue";
import { useGraphStore } from "../../../store/graph";
import { useDomainStore } from "../../../store/domain";
import { useDebugStore } from "../../../store/debug";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import QuestionInput from "./QuestionInput.vue";
import AskQuestionButton from "./AskQuestionButton.vue";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";
import { marked } from "marked";

const graphStore = useGraphStore();
const domainStore = useDomainStore();
const debugStore = useDebugStore();
const instanceWorkspacesStore = useInstanceWorkspacesStore();

const compProps = defineProps({
	title: {
		type: String,
		required: true,
	},
	subtitle: {
		type: String,
		required: true,
	},
	showUnknownButton: {
		type: String,
		required: false,
		default: "default",
	},
	playButton: {
		type: String,
		required: false,
		default: "default",
	},
	instanceTask: {
		type: Object as PropType<InstanceTask>,
		required: true,
	},
});

const outputAttributes = computed(() => {
	return compProps.instanceTask.outputAttributes
		.map((attrId) => graphStore.instance.attribute.byId(attrId) as InstanceAttribute)
		.filter((attr) => attr !== undefined);
});

function openDesignerProcess() {
	const protocol = window.location.protocol === "http:" ? "http" : "https";
	const host = `designer.notitia.dev`;
	const { id: domainId } = domainStore.domain;
	const templateTask = graphStore.template.task.byId(compProps.instanceTask.template);
	if (!templateTask) return;
	const templateProcess = graphStore.template.processes.find((p) => p.tasks.includes(templateTask.id));
	if (!templateProcess) return;
	const url = `${protocol}://${host}/${domainId}/template/processes/${templateProcess.id}?task-id=${templateTask.id}`;
	window.open(url, "_blank");
}

function isPinned(): boolean {
	return compProps.instanceTask.pinned;
}

function displayPlayButton(): boolean {
	// individual settings
	if (compProps.playButton === "show") return true;
	if (compProps.playButton === "hide") return false;

	// global settings
	const templateWorkspace = graphStore.template.workspace.byId(
		instanceWorkspacesStore.openedWorkspace?.template || ""
	);
	if (templateWorkspace?.context?.show_askQuestionButton === false) return false;
	if (templateWorkspace?.context?.show_askQuestionButton === true) return true;

	// default
	return true;
}

onMounted(() => {
	window.addEventListener("keydown", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(true);
	});
	window.addEventListener("keyup", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(false);
	});
});
onUnmounted(() => {
	window.removeEventListener("keydown", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(true);
	});
	window.removeEventListener("keyup", (e) => {
		if (e.key === "Alt") debugStore.setAltKeyHeld(false);
	});
});
</script>

<style scoped lang="scss">
.multiline {
	white-space: pre-wrap;
}

.markdown-content {
	:deep(p) {
		margin-bottom: 0px;
	}
	:deep(h1) {
		font-size: 1.35rem !important;
		font-weight: bold;
		margin-bottom: 4px;
		line-height: 1.4;
	}
	:deep(h2) {
		font-size: 1.25rem !important;
		font-weight: bold;
		margin-bottom: 4px;
		line-height: 1.4;
	}
	:deep(h3) {
		font-size: 1.15rem !important;
		font-weight: bold;
		margin-bottom: 4px;
		line-height: 1.4;
	}
	:deep(h4),
	:deep(h5),
	:deep(h6) {
		font-size: 1.05rem !important;
		font-weight: bold;
		margin-bottom: 4px;
		line-height: 1.4;
	}
	:deep(ul),
	:deep(ol) {
		padding-left: 20px;
		margin-bottom: 8px;
	}

	:deep(table) {
		border-collapse: collapse;
		margin: 15px 0;
	}
	:deep(th),
	:deep(td) {
		border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
		padding: 6px 13px;
	}

	:deep(blockquote) {
		border-left: 4px solid rgba(var(--v-theme-on-surface), 0.2);
		padding-left: 15px;
		color: rgba(var(--v-theme-on-surface), 0.7);
		margin: 15px 0;
	}

	:deep(pre) {
		background-color: rgba(var(--v-theme-on-surface), 0.05);
		padding: 10px;
		border-radius: 4px;
		overflow: auto;
		margin: 15px 0;
		font-family: monospace;

		code {
			background-color: transparent;
			padding: 0;
		}
	}

	:deep(code) {
		background-color: rgba(var(--v-theme-on-surface), 0.05);
		padding: 2px 4px;
		border-radius: 3px;
		font-family: monospace;
	}
}
</style>
